import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTable, useFilters, usePagination, useSortBy } from "react-table";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { compose } from "redux";
import { Redirect } from "react-router-dom";
import { RiTableFill, RiTableLine, RiGridFill, RiGridLine } from "react-icons/ri";
import { PiCalendarDotsFill, PiCalendarDots } from "react-icons/pi";
import moment from "moment";
import CalendarView from "../../components/Control/CalendarView";
import LOADER from "../../assets/images/loader.svg";
import { getControls_Report, editControl, getControls_Search } from "../../store/actions/riskActions";
import Popup from 'reactjs-popup';
import ControlOverlay from "../../components/Control/ControlOverlay";
import TopBar from "../../components/TopBar";
import Sidebar from "../../components/Sidebar";
import ControlsHeader from "../../components/Control/ControlsHeader";
import "../../components/Control/controls.theme.css";
import { FiAlertTriangle, FiXCircle, FiClock, FiCheckCircle } from "react-icons/fi";



function AllControls(props) {
  const { token, controls, activeDepartmentId, getControlsReportFN, getControlsSearchFN, editControlFN } = props;
  const { t } = useTranslation();

  const labelControlTypeDisplay = useCallback(
    (value) => {
      if (!value) return null;
      const key = value.toString().trim().toLowerCase();
      const known = ["preventive", "detective", "mitigative", "corrective", "directive"];
      return known.includes(key) ? t(`controlManagement.filters.${key}`) : value;
    },
    [t]
  );

  const labelPeriodDisplay = useCallback(
    (value) => {
      if (!value) return null;
      const key = value.toString().trim().toLowerCase();
      if (key === "daily") return t("controlManagement.period.daily");
      if (key === "monthly") return t("controlManagement.period.monthly");
      if (key === "quarterly") return t("controlManagement.period.quarterly");
      return value;
    },
    [t]
  );

  const effectivenessDisplay = useCallback((raw) => {
    const v = Number(raw ?? 0);
    if (v >= 75) return { label: t("controlManagement.filters.high"), pillClass: "bg-black text-white" };
    if (v >= 40) return { label: t("controlManagement.filters.medium"), pillClass: "bg-gray-200 text-gray-800" };
    return { label: t("controlManagement.filters.low"), pillClass: "bg-gray-100 text-gray-700" };
  }, [t]);

  const [loading, setLoading] = useState(false);
  const [flattenedControls, setFlattenedControls] = useState([]);
  const [selectedView, setSelectedView] = useState("table");
  const [viewMode, setViewMode] = useState("table");
  const [isControlOpen, setIsControlOpen] = useState(false);
  const [control_id, setControlId] = useState(null);
  const [risk_id, setRiskId] = useState(null);

  const [query, setQuery] = useState("");
  const [typeView, setTypeView] = useState("All");
  const [effView, setEffView] = useState("All");
  const [statusView, setStatusView] = useState("All");
  const [calendarDate, setCalendarDate] = useState(new Date());



  useEffect(() => {
    ////console.log(activeDepartmentId)
    if (activeDepartmentId === null) {
      setLoading(true);
      getControlsSearchFN(null).finally(() => setLoading(false));
      //////console.log(controls);
    }
  }, [activeDepartmentId, getControlsSearchFN]);

  useEffect(() => {
    if (controls && controls?.length > 0) {
      //////console.log("Controls before flat:" , controls);
      const allControls = controls
        .filter((control) =>
          control.controls.some(
            (c) =>
              c.controlDetails !== null ||
              c.controlType !== null ||
              c.controlDefinition !== null
          )

        )
        .flatMap((control) =>
          control.controls.map((c) => ({
            ...c,
            riskId: control.riskId,
            title: control.title,
            owner: c.owner,
            is_secondline: c.secondline
          }))
        );
      setFlattenedControls(allControls);
    } else {
      setFlattenedControls([]);
    }
  }, [controls]);

  const closeCreation = async (data, control_id) => {
    ////console.log("Data:", data, "ID", control_id)

    setIsControlOpen(false)
    await updateServerData(data, control_id);
    getControlsSearchFN(null)

  }

  const updateServerData = async (data, control_id) => {


    const updatedData = { ...data };


    if (updatedData.StartDate) {
      const startDate = new Date(updatedData.StartDate);
      startDate.setDate(startDate.getDate() + 1);
      updatedData.StartDate = startDate.toISOString().split('T')[0];
    }

    if (updatedData.EndDate) {
      const endDate = new Date(updatedData.EndDate);
      endDate.setDate(endDate.getDate() + 1);
      updatedData.EndDate = endDate.toISOString().split('T')[0];
    }

    //////console.log("CONTROL DATA AFTER ADJUSTMENT", updatedData);

    setLoading(true);
    await editControlFN(risk_id, control_id, updatedData);
    setLoading(false);

  }

  const compliancePillClass = (t) => {
    const map = {
      "NIST": "bg-blue-100 text-blue-800 border-blue-300",
      "COBIT": "bg-purple-100 text-purple-800 border-purple-300",
      "ISO 27001": "bg-emerald-100 text-emerald-800 border-emerald-300",
      "GDPR": "bg-amber-100 text-amber-900 border-amber-300",
    };
    return map[t] || "bg-gray-100 text-gray-800 border-gray-300";
  };
  const typePillClass = (t) => {
    const key = (t || "").toString().trim().toLowerCase();
    if (key === "preventive") return "pill-blue";
    if (key === "detective") return "pill-purple";
    if (key === "corrective") return "pill-orange";
    if (key === "mitigative") return "pill-red";
    if (key === "directive") return "pill-green";
    return "badge-soft-gray";
  };

  const columns = React.useMemo(
    () => [
      {
        Header: t("controlManagement.headers.controlName"),
        accessor: "controlDetails",
        id: "controlDetails",
        columnWidth: 280,
        Cell: ({ value, row }) => (
          <div className="flex flex-col min-w-[240px] max-w-[500px] pr-2">
            <span className="font-medium text-gray-800 text-sm">
              {value || "-"}
            </span>

            {row.original.controlDefinition && (
              <span className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                {row.original.controlDefinition}
              </span>
            )}
          </div>
        ),
      },

      {
        Header: t("controlManagement.headers.type"),
        accessor: "controlType",
        id: "controlType",
        columnWidth: 110,
        Cell: ({ value }) =>
          value ? (
            <span
              className={`px-2 py-0.5 rounded-full text-xs font-medium ${typePillClass(
                value
              )}`}
            >
              {labelControlTypeDisplay(value)}
            </span>
          ) : (
            <span className="text-xs text-gray-400">-</span>
          ),
      },

      {
        Header: t("controlManagement.headers.frameworks"),
        accessor: "complianceTypes",
        id: "frameworks",
        columnWidth: 200,
        Cell: ({ value }) => (
          <div className="flex flex-wrap gap-1">
            {Array.isArray(value) && value?.length > 0 ? (
              value.map((c, i) => (
                <span
                  key={i}
                  className={`px-2 py-0.5 text-xs rounded-full border ${compliancePillClass(
                    c
                  )}`}
                >
                  {c}
                </span>
              ))
            ) : (
              <span className="text-gray-400 text-xs">{t("controlManagement.noFramework")}</span>
            )}
          </div>
        ),
      },

      {
        Header: t("controlManagement.headers.findings"),
        accessor: "findingNumber",
        id: "findings",
        columnWidth: 90,
        Cell: ({ value }) => {
          const num = Number(value ?? 0);
          if (isNaN(num))
            return <span className="text-xs text-gray-500">-</span>;
          return (
            <span
              className={
                num === 0
                  ? "text-green-600 text-sm"
                  : "text-red-600 font-semibold text-sm"
              }
            >
              {num}
            </span>
          );
        },
      },
      {
        Header: t("controlManagement.headers.riskIds"),
        accessor: "RiskIds",
        id: "riskIds",
        columnWidth: 160,
        Cell: ({ value }) => {
          const ids = Array.isArray(value) ? value : [];
          if (ids.length === 0) return "-";
          return (
            <div className="flex flex-wrap gap-1">
              {ids.slice(0, 3).map((id) => (
                <span
                  key={id}
                  className="px-2 py-[2px] rounded-full text-[12px] bg-gray-100 border border-gray-200 text-gray-800"
                >
                  {id}
                </span>
              ))}
              {ids.length > 3 ? (
                <span className="text-[12px] text-gray-500">+{ids.length - 3}</span>
              ) : null}
            </div>
          );
        },
      },

      {
        Header: t("controlManagement.headers.effectiveness"),
        accessor: "efficiencyLevel",
        id: "efficiencyLevel",
        columnWidth: 120,
        Cell: ({ value, row }) => {
          const raw =
            typeof value === "number"
              ? value
              : Number(
                row.original.efficiencyLevel ??
                row.original.controlEfficiency ??
                row.original.efficiency ??
                0
              );

          const { label, pillClass } = effectivenessDisplay(raw);

          return (
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium ${pillClass}`}
            >
              {label}
            </span>
          );
        },
      },

      {
        Header: t("controlManagement.headers.owner"),
        accessor: (row) =>
          `${row.owner?.firstName || ""} ${row.owner?.lastName || ""}`.trim(),
        id: "owner",
        columnWidth: 140,
        Cell: ({ value }) => (
          <span className="text-sm text-gray-800">{value || "-"}</span>
        ),
      },

      {
        Header: t("controlManagement.headers.period"),
        accessor: "controlPeriod",
        id: "controlPeriod",
        columnWidth: 100,
        Cell: ({ value }) =>
          value ? (
            <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs">
              {labelPeriodDisplay(value)}
            </span>
          ) : (
            <span className="text-xs text-gray-400">-</span>
          ),
      },
    ],
    [t, labelControlTypeDisplay, labelPeriodDisplay, effectivenessDisplay]
  );


  const counts = useMemo(() => {
    const normType = (s = "") => s?.toString().trim().toLowerCase();
    let total = flattenedControls?.length || 0;
    let highEff = 0;
    let preventive = 0;
    let detective = 0;

    (flattenedControls || []).forEach((c) => {
      const eff = Number(c.efficiencyLevel ?? 0);
      if (eff >= 75) highEff++;
      const t = normType(c.controlType);
      if (t === "preventive") preventive++;
      if (t === "detective") detective++;
    });

    return { total, highEff, preventive, detective };
  }, [flattenedControls]);

  // Filters + search
  const effBucket = (n) => {
    const v = Number(n ?? 0);
    if (v >= 75) return "high";
    if (v >= 40) return "medium";
    return "low";
  };
  const filteredControls = useMemo(() => {
    const q = (query || "").toLowerCase().trim();
    const norm = (v) => (v ?? "").toString().toLowerCase();
    const wantType = (typeView || "All").toLowerCase();
    const wantEff = (effView || "All").toLowerCase();
    const wantStatus = (statusView || "All").toLowerCase();
    const statusOf = (c) => {
      const now = new Date();
      const s = c.startDate ? new Date(c.startDate) : null;
      const e = c.endDate ? new Date(c.endDate) : null;
      if (e && e < now) return "closed";
      if (s && s > now) return "open";
      return "in progress";
    };
    return (flattenedControls || []).filter((c) => {
      if (q) {
        const hay = [
          norm(c.controlID),
          norm(c.controlDetails),
          norm(c.controlDefinition),
          norm(Array.isArray(c.complianceTypes) ? c.complianceTypes.join(",") : c.complianceTypesRaw || ""),
          norm(c.owner?.firstName), norm(c.owner?.lastName),
          norm(c.controlType),
        ].join(" ");
        if (!hay.includes(q)) return false;
      }
      if (wantType !== "all" && (c.controlType || "").toLowerCase() !== wantType) return false;
      if (wantEff !== "all" && effBucket(c.efficiencyLevel) !== wantEff) return false;
      if (wantStatus !== "all" && statusOf(c) !== wantStatus) return false;
      return true;
    });
  }, [flattenedControls, query, typeView, effView, statusView]);

  //////console.log("flattened controls:", flattenedControls);
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    gotoPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: filteredControls,
      initialState: { pageIndex: 0, pageSize: 10 },
      autoResetPage: false,
      autoResetSortBy: false,
      autoResetFilters: false,
    },
    useFilters,
    useSortBy,
    usePagination
  );

  const renderPagination = () => {
    const total = filteredControls?.length || 0;
    const from = total === 0 ? 0 : pageIndex * pageSize + 1;
    const to = Math.min((pageIndex + 1) * pageSize, total);
    return (
      <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-6 py-3 mt-4">
        <div className="text-sm text-gray-600">
          {t("controlManagement.pagination.showingRange", { from, to, total })}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => gotoPage(0)} disabled={!canPreviousPage} className="px-2 py-1 text-sm text-gray-600 disabled:opacity-40">«</button>
          <button onClick={() => previousPage()} disabled={!canPreviousPage} className="px-2 py-1 text-sm text-gray-600 disabled:opacity-40">{t("controlManagement.pagination.previous")}</button>
          <button onClick={() => gotoPage(pageIndex)} className="px-3 py-1 text-sm bg-black text-white rounded">{pageIndex + 1}</button>
          {Math.ceil(total / pageSize) > pageIndex + 1 && (
            <button onClick={() => gotoPage(pageIndex + 1)} className="px-3 py-1 text-sm text-gray-700 rounded hover:bg-gray-100">{pageIndex + 2}</button>
          )}
          <button onClick={() => nextPage()} disabled={!canNextPage} className="px-2 py-1 text-sm text-gray-600 disabled:opacity-40">{t("controlManagement.pagination.next")}</button>
          <button onClick={() => gotoPage(Math.max(0, Math.ceil(total / pageSize) - 1))} disabled={!canNextPage} className="px-2 py-1 text-sm text-gray-600 disabled:opacity-40">»</button>
        </div>
        <div className="text-sm text-gray-600">
          {t("controlManagement.pagination.perPage", { size: pageSize })}
        </div>
      </div>
    );
  };

  //////console.log("RISK ID FOR CONTROL", controls.risk_id);

  function StatCard({ label, value }) {
    let Icon;
    let iconColor = "";

    switch (label) {
      case "Total Findings":
        Icon = FiAlertTriangle;
        iconColor = "text-gray-400";
        break;
      case "In Progress":
        Icon = FiXCircle;
        iconColor = "text-red-500";
        break;
      case "Open Findings":
        Icon = FiClock;
        iconColor = "text-blue-500";
        break;
      case "Closed":
        Icon = FiCheckCircle;
        iconColor = "text-green-500";
        break;
      default:
        Icon = null;
    }

    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4 flex flex-col justify-between">
        <div className="flex items-center justify-between">
          <div className="text-xs text-gray-500">{label}</div>
          {Icon && <Icon className={`w-4 h-4 ${iconColor}`} />}
        </div>
        <div className="mt-1 text-2xl text-[#0E0E0E]">{value}</div>
      </div>
    );
  }

  return token ? (
    <div className="w-full h-screen flex flex-col bg-[#F8F8F8] overflow-x-hidden">
      {/* Top bar - fixed in layout, not via position:fixed */}
      <div className="flex-shrink-0">
        <TopBar />
      </div>

      {/* Main area: sidebar + scrollable content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - fixed in layout on the left */}
        <div className="flex-shrink-0 h-full">
          <Sidebar className="h-full" />
        </div>

        {/* MAIN SCROLL AREA */}
        <div className="flex-1 overflow-y-auto">
          <div className="w-full max-w-[1700px] mx-auto px-4 pb-6 pt-6">
            {loading ? (
              <img src={LOADER} alt="Loading..." className="spinner" />
            ) : (
              <>
                <div className="px-0 pt-2 sm:pt-6 pb-2">
                  <div className="w-full flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-3">
                    <div className="min-w-0">
                      <h1 className="text-lg sm:text-xl md:text-2xl font-medium tracking-wide text-[#0E0E0E]">
                        {selectedView === "table"
                          ? t("controlManagement.allControlsPage.title")
                          : t("controlManagement.calendarTitle")}
                      </h1>
                      <p className="text-xs md:text-sm text-gray-600">
                        {selectedView === "table"
                          ? t("controlManagement.allControlsPage.subtitle")
                          : t("controlManagement.calendarSubtitle")}
                      </p>
                    </div>
                    <div className="flex items-center gap-0 border border-gray-300 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setSelectedView("table")}
                        className={`flex items-center gap-2 border-r border-gray-300 px-3.5 py-1.5 text-sm font-medium transition ${selectedView === "table"
                          ? "bg-[#0E0E0E] text-white"
                          : "bg-white text-gray-600 hover:bg-gray-50"
                          }`}
                      >
                        {selectedView === "table" ? (
                          <RiGridFill size={16} />
                        ) : (
                          <RiGridLine size={16} />
                        )}
                        <span>{t("controlManagement.toggleControls")}</span>
                      </button>

                      <button
                        onClick={() => setSelectedView("calendar")}
                        className={`flex items-center gap-2 px-3.5 py-1.5 text-sm font-medium transition ${selectedView === "calendar"
                          ? "bg-[#0E0E0E] text-white"
                          : "bg-white text-gray-600 hover:bg-gray-50"
                          }`}
                      >
                        {selectedView === "calendar" ? (
                          <PiCalendarDotsFill size={16} />
                        ) : (
                          <PiCalendarDots size={16} />
                        )}
                        <span>{t("controlManagement.toggleCalendar")}</span>
                      </button>
                    </div>
                  </div>
                  {selectedView === "table" ? (
                   <>
                   </>
                  ) : (
                    <div className="bg-white border border-gray-200 rounded-xl p-4 mt-2 mb-4 shadow-sm">
                      <div className="flex items-center justify-end">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => {
                              const newDate = moment(calendarDate).subtract(1, "month").toDate();
                              setCalendarDate(newDate);
                            }}
                            className="flex items-center gap-2 px-3.5 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition"
                          >
                            <span>{t("controlManagement.pagination.previous")}</span>
                          </button>
                          <button
                            onClick={() => {
                              const newDate = moment(calendarDate).add(1, "month").toDate();
                              setCalendarDate(newDate);
                            }}
                            className="flex items-center gap-2 px-3.5 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition"
                          >
                            <span>{t("controlManagement.pagination.next")}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {selectedView === "table" && (
                  <>
                    {/* Stat Cards */}
                    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      <StatCard label={t("controlManagement.summary.totalControls")} value={counts.total} />
                      <StatCard label={t("controlManagement.summary.highEffectiveness")} value={counts.highEff} />
                      <StatCard label={t("controlManagement.summary.preventive")} value={counts.preventive} />
                      <StatCard label={t("controlManagement.summary.detective")} value={counts.detective} />
                    </div>

                    {/* Filters + viewMode toggle */}
                    <div className="mt-5">
                      <ControlsHeader
                        query={query}
                        setQuery={setQuery}
                        typeView={typeView}
                        setTypeView={setTypeView}
                        effView={effView}
                        setEffView={setEffView}
                        statusView={statusView}
                        setStatusView={setStatusView}
                        viewMode={viewMode}
                        setViewMode={setViewMode}
                        showToggle={true}
                        searchPlaceholderKey="controlManagement.allControlsPage.searchPlaceholder"
                      />
                    </div>

                    {/* GRID vs TABLE SWITCH */}
                    {viewMode === "grid" ? (
                      <>
                        <div className="mt-4 shadow-xl bg-white rounded-2xl border border-gray-200 overflow-hidden">

                          {/* Header ÔÇö divider REMOVED */}
                          <div className="px-6 py-4 bg-white">
                            <h3 className="text-gray-900">{t("controlManagement.list.title")}</h3>
                            <p className="text-sm text-gray-500">
                              {t("controlManagement.list.controlsFound", { count: filteredControls?.length ?? 0 })}
                            </p>
                          </div>

                          {/* ­şæç Wrap grid + divider inside SAME padding block */}
                          <div className="px-4 pb-4">

                            {/* Grid wrapper inside the rounded card */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 py-4 items-stretch">
                              {page.map((row) => {
                                prepareRow(row);
                                const control = row.original;

                                const num = Number(control.findingNumber ?? 0);
                                const findingCls = isNaN(num)
                                  ? ""
                                  : num === 0
                                    ? "badge-green"
                                    : "badge-red";

                                const effRaw = Number(
                                  control.efficiencyLevel ??
                                  control.controlEfficiency ??
                                  control.efficiency ??
                                  0
                                );
                                const effLabel = effectivenessDisplay(effRaw).label;
                                const effColorClass =
                                  effRaw >= 75
                                    ? "bg-green-700 text-white"
                                    : effRaw >= 40
                                      ? "bg-yellow-600 text-white"
                                      : "bg-red-700 text-white";

                                const ownerName =
                                  `${control.owner?.firstName || ""} ${control.owner?.lastName || ""
                                    }`.trim() || "-";

                                return (
                                  <div
                                    key={control.controlID}
                                    onClick={() => {
                                      const matchingRisk = controls.find((risk) =>
                                        risk.controls?.some(
                                          (ctrl) => ctrl.controlID === control.controlID
                                        )
                                      );
                                      if (matchingRisk) setRiskId(matchingRisk.riskId);
                                      setControlId(control.controlID);
                                      setTimeout(() => setIsControlOpen(true), 0);
                                    }}
                                    className="bg-white border border-gray-200 rounded-lg px-2 py-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer flex flex-col min-h-[280px]"
                                  >
                                    <div className="flex items-start justify-between mb-1.5">
                                      <span className="text-xs text-gray-500">{`CTRL-${control.controlID ?? ""
                                        }`}</span>
                                      {control.controlPeriod && (
                                        <span className="px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-700">
                                          {labelPeriodDisplay(control.controlPeriod)}
                                        </span>
                                      )}
                                    </div>

                                    <h4 className="font-medium text-gray-800 mb-2 line-clamp-2">
                                      {control.controlDetails || "-"}
                                    </h4>

                                    {control.controlDefinition && (
                                      <p className="text-xs text-gray-500 mb-2 line-clamp-2">
                                        {control.controlDefinition}
                                      </p>
                                    )}

                                    <div className="flex flex-col gap-1.5 mb-2">
                                      <div className="flex items-center gap-1.5">
                                        {control.controlType && (
                                          <span
                                            className={`px-2 py-1 rounded-lg text-xs ${typePillClass(
                                              control.controlType
                                            )}`}
                                          >
                                            {labelControlTypeDisplay(control.controlType)}
                                          </span>
                                        )}
                                        <span
                                          className={`${effColorClass} px-1.5 py-1 rounded text-xs font-medium`}
                                        >
                                          {effLabel}
                                        </span>
                                      </div>

                                      <div className="flex flex-wrap gap-0.5 justify-start">
                                        {Array.isArray(control.complianceTypes) &&
                                          control.complianceTypes?.length > 0 ? (
                                          control.complianceTypes.map((c, i) => (
                                            <span
                                              key={i}
                                              className="bg-gray-100 text-gray-700 border border-gray-300 px-1.5 py-0.5 rounded text-xs"
                                            >
                                              {c}
                                            </span>
                                          ))
                                        ) : (
                                          <span className="text-gray-400 text-xs">{t("controlManagement.noFramework")}</span>
                                        )}
                                      </div>
                                    </div>

                                    <div className="flex flex-col gap-1 pt-2 border-t border-gray-100 mt-auto">
                                      <div>
                                        <span className="text-xs text-gray-500">{t("controlManagement.grid.ownerLabel")} </span>
                                        <span className="text-xs text-gray-700">{ownerName}</span>
                                      </div>
                                      <div>
                                        <span className="text-xs text-gray-500">{t("controlManagement.grid.findingsLabel")} </span>
                                        <span className={`text-xs ${isNaN(num) ? "" : findingCls}`}>
                                          {isNaN(num) ? "-" : num}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="text-xs text-gray-500">{t("controlManagement.grid.lastReviewedLabel")} </span>
                                        <span className="text-xs text-gray-700">
                                          {control.lastReviewDate
                                            ? new Date(control.lastReviewDate)
                                              .toLocaleDateString("en-GB", {
                                                day: "2-digit",
                                                month: "2-digit",
                                                year: "numeric",
                                              })
                                              .replace(/\//g, ".")
                                            : control.reviewDate
                                              ? new Date(control.reviewDate)
                                                .toLocaleDateString("en-GB", {
                                                  day: "2-digit",
                                                  month: "2-digit",
                                                  year: "numeric",
                                                })
                                                .replace(/\//g, ".")
                                              : "-"}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>

                            {/* Divider + pagination INSIDE the same px-4 wrapper */}
                            <div className="border-t border-gray-200 mt-3 pt-3 mb-4">
                              {renderPagination()}
                            </div>

                          </div>
                        </div>
                      </>




                    ) : (
                      <>
                        <div className="shadow-xl bg-white rounded-2xl border border-gray-200 mt-6">

                          {/* Header */}
                          <div className="px-6 py-4">
                            <h3 className="text-gray-900">{t("controlManagement.list.title")}</h3>
                            <p className="text-sm text-gray-500">
                              {t("controlManagement.list.controlsFoundWithPage", {
                                count: filteredControls?.length ?? 0,
                                page: pageIndex + 1,
                                total: Math.max(1, Math.ceil((filteredControls?.length || 0) / pageSize)),
                              })}
                            </p>
                          </div>

                          {/* Inner padded content */}
                          <div className="px-4 pt-4 pb-4">

                            {/* Table wrapper */}
                            <div className="border border-gray-200 rounded-xl overflow-hidden">
                              <table
                                {...getTableProps()}
                                className="w-full text-sm text-gray-800"
                                style={{
                                  borderCollapse: "separate",
                                  borderSpacing: 0,
                                  tableLayout: "fixed",
                                }}
                              >
                                <thead>
                                  {headerGroups.map((headerGroup) => {
                                    const cols = headerGroup.headers;

                                    return (
                                      <tr
                                        {...headerGroup.getHeaderGroupProps()}
                                        className="border-b border-gray-300 bg-white"
                                      >
                                        {/* Control ID (static first column) */}
                                        <th
                                          className="text-left text-[13px] text-gray-800 font-semibold"
                                          style={{
                                            background: "#fff",
                                            padding: "0.3rem 0.75rem",
                                            width: "110px",
                                            minWidth: "110px",
                                            maxWidth: "110px",

                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {t("controlManagement.headers.controlId")}
                                        </th>

                                        {/* Dynamic headers from columns[] */}
                                        {cols.map((column) => {
                                          if (column.id === "riskTitle") return null;

                                          const w = column.columnWidth;

                                          return (
                                            <th
                                              {...column.getHeaderProps(column.getSortByToggleProps())}
                                              className="cursor-pointer text-left text-[13px] text-gray-800 font-semibold"
                                              style={{
                                                background: "#fff",
                                                padding: "0.3rem 0.75rem",
                                                width: w ? `${w}px` : "auto",
                                                minWidth: w ? `${w}px` : undefined,
                                                maxWidth: w ? `${w}px` : undefined,
                                              }}
                                            >
                                              <div className="flex items-center gap-1">
                                                {column.render("Header")}
                                                <span className="opacity-70">
                                                  {column.isSorted
                                                    ? column.isSortedDesc
                                                      ? " ­şö¢"
                                                      : " ­şö╝"
                                                    : ""}
                                                </span>
                                              </div>
                                            </th>
                                          );
                                        })}
                                      </tr>
                                    );
                                  })}
                                </thead>


                                <tbody {...getTableBodyProps()}>
                                  {page?.length === 0 ? (
                                    /* your empty state here */
                                    <>
                                      {/* ... unchanged ... */}
                                    </>
                                  ) : (
                                    page.map((row) => {
                                      prepareRow(row);
                                      return (
                                        <tr
                                          {...row.getRowProps({
                                            onClick: () => {
                                              const matchingRisk = controls.find((risk) =>
                                                risk.controls?.some(
                                                  (ctrl) => ctrl.controlID === row.original.controlID
                                                )
                                              );
                                              if (matchingRisk) setRiskId(matchingRisk.riskId);
                                              setControlId(row.original.controlID);
                                              setTimeout(() => setIsControlOpen(true), 0);
                                            },
                                          })}
                                          className="group transition-colors duration-200 cursor-pointer"
                                        >
                                          {/* Control ID */}
                                          <td
                                            className="border-t border-gray-200 group-hover:bg-[#F8F8F8] whitespace-nowrap"
                                            style={{ padding: "0.65rem 1rem", width: 110 }}
                                          >
                                            {`CTRL-${row.original.controlID ?? ""}`}
                                          </td>

                                          {/* Dynamic cells */}
                                          {row.cells.map((cell) => {
                                            if (cell.column.id === "riskTitle") return null;

                                            return (
                                              <td
                                                {...cell.getCellProps()}
                                                className="border-t border-gray-200 group-hover:bg-[#F8F8F8] align-top"
                                                style={{ padding: "0.65rem 1rem" }}
                                              >
                                                {cell.render("Cell")}
                                              </td>
                                            );
                                          })}
                                        </tr>
                                      );
                                    })
                                  )}
                                </tbody>
                              </table>
                            </div>

                            {/* Divider + pagination ÔÇô no extra padding under line */}
                            <div className="border-t border-gray-200 mt-3">
                              {renderPagination()}
                            </div>
                          </div>
                        </div>
                      </>






                    )}

                  </>
                )}

                 {selectedView === "calendar" && (
                <>
                  

                  <div className="mt-4 flex justify-center">
                    <div className="w-full max-w-[1700px]">
                      <CalendarView
                        events={filteredControls}
                        currentDate={calendarDate}
                        onNavigate={setCalendarDate}
                      />
                    </div>
                  </div>
                </>
              )}
              </>
            )}

            <Popup
              open={isControlOpen}
              onClose={() => setIsControlOpen(false)}
              contentStyle={{ overflow: 'auto', maxHeight: '80vh', width: '900px', marginTop: '14vh' }}
            >
              <ControlOverlay key={control_id || 'new-control'} closeControl={closeCreation} control_id={control_id} risk_id={risk_id} />
            </Popup>

          </div>
        </div>
      </div>
    </div>
  ) : (
    <Redirect to="/login" />
  );
}

const mapStateToProps = (state) => {
  ////console.log("Controls:", state.risk.risk_controls)
  return {
    token: state.auth.token,
    controls: state.risk.all_risk_controls,
    activeDepartmentId: state.department.activeDepartmentId,
  }
};

const mapDispatchToProps = (dispatch) => ({
  getControlsReportFN: (departmentId) => dispatch(getControls_Report(departmentId)),
  getControlsSearchFN: (departmentId) => dispatch(getControls_Search(departmentId)),
  editControlFN: (risk_id, control_id, control) => dispatch(editControl(risk_id, control_id, control)),
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(AllControls);